import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient, apiErrorMessage, callFunction } from "../api/client";
import type {
  ProcessingGrid, ProcessingKind, ProcessingLookupWindow, ProcessingPriorityChoice, ProcessingSpend, ProcessingStatus,
  ProcessingSwitch, ProcessingWorker, SettingChange,
} from "../api/types";
import { VIEW_COLUMNS } from "../api/types";
import type { SignedIn } from "../lib/auth";
import { count, loadedAt, shortDate } from "../lib/format";
import { Spinner } from "../components/Spinner";
import { Panel } from "../components/Panel";
import { RecordLink } from "../components/RecordLink";
import { Sparkline } from "../components/Sparkline";
import { StackedBars, type Bucket } from "../components/StackedBars";
import {
  kindRank, PRIORITY_WORDS, STAGES, WINDOWS, type PassRow, type ScheduleRow, type SampleRow, type SeriesRow,
  type Window, type WindowRow, agoWords, byDay, changeFromToWords, changeSentence, changeWaiting, changeWords, clearsWords, durationWords, effectWords, fromLocalInput,
  cents, kindMixWords, kindsIn, limitSentence, localInputValue, localTime, money, outcomeWords, paceChanges, paceWords,
  priorityWords, readWindow, reasonLines, rememberWindow, seriesFor, statusSentence, utcDay, whenWords,
  windowLabel, windowTotal, cellWords, healthWords, needsALook, scheduleOrder, scheduleSummary, CELL_WORDS, everyWords,
} from "../lib/processing";

/* Admin: Processing. What the engine is doing, how fast, what it costs, and
 * where it is stuck, from the admin reads engine migrations 164 to 174 added,
 * with the controls that change it. The plan is docs/plans/processing-screen.md.
 *
 * Types of work (who asked for it) are rows and the engine's stages are
 * columns, and a cell is the count waiting. Every number is a fact from a
 * view, and nothing here decides a state, a rate or an expected finish: the
 * status sentence, the clog, Busy and Clears are the engine's. Every control
 * calls an engine setter and shows its refusal as written. */

/* Every column of a read, from the generated VIEW_COLUMNS, so a column a
   migration adds reaches the screen with no edit here. Hand-written lists
   left columns out twice: the worker list lacked the pace bounds (engine
   migration 170) and the status list lacked the reserve and who paused
   (171). Both looked right in a browser while the reserve was 0 and nothing
   was paused. Asked inside each query, so a view missing from the generated
   types fails that read with its name rather than the whole app. */
function columns(view: string): string {
  const c = VIEW_COLUMNS[view];
  if (!c) throw new Error(`The generated API types have no view ${view}. Run scripts/generate-api-types.sh.`);
  return c.join(",");
}

type Cell = { stage: string; kind: string };

export function Processing({ session }: { session: SignedIn }) {
  const token = session.idToken;
  const queryClient = useQueryClient();
  const [win, setWin] = useState<Window>(readWindow);
  useEffect(() => { rememberWindow(win); }, [win]);
  const [cell, setCell] = useState<Cell | null>(null);
  const [kindOpen, setKindOpen] = useState<string | null>(null);
  const [paceOpen, setPaceOpen] = useState<string | null>(null);
  const [jobOpen, setJobOpen] = useState<string | null>(null);
  const [limitOpen, setLimitOpen] = useState(false);

  const status = useQuery({
    queryKey: ["processing", "status"],
    refetchInterval: 60_000,
    queryFn: async () => {
      const { data, error } = await apiClient(token).from("processing_status").select(columns("processing_status")).limit(1).single();
      if (error) throw new Error(apiErrorMessage(error));
      return data as ProcessingStatus;
    },
  });
  const grid = useQuery({
    queryKey: ["processing", "grid"],
    refetchInterval: 60_000,
    queryFn: async () => {
      const { data, error } = await apiClient(token).from("processing_grid").select(columns("processing_grid"))
        .order("worker_order").limit(200);
      if (error) throw new Error(apiErrorMessage(error));
      return (data ?? []) as ProcessingGrid[];
    },
  });
  const workers = useQuery({
    queryKey: ["processing", "workers"],
    refetchInterval: 60_000,
    queryFn: async () => {
      const { data, error } = await apiClient(token).from("processing_worker").select(columns("processing_worker")).limit(100);
      if (error) throw new Error(apiErrorMessage(error));
      return (data ?? []) as ProcessingWorker[];
    },
  });
  const kinds = useQuery({
    queryKey: ["processing", "kinds"],
    refetchInterval: 60_000,
    queryFn: async () => {
      const { data, error } = await apiClient(token).from("processing_kind").select(columns("processing_kind")).limit(50);
      if (error) throw new Error(apiErrorMessage(error));
      return (data ?? []) as ProcessingKind[];
    },
  });
  const choices = useQuery({
    queryKey: ["processing", "choices"],
    refetchInterval: 60_000,
    queryFn: async () => {
      const { data, error } = await apiClient(token).from("processing_priority_choice").select(columns("processing_priority_choice"))
        .order("choice_order").limit(200);
      if (error) throw new Error(apiErrorMessage(error));
      return (data ?? []) as ProcessingPriorityChoice[];
    },
  });
  const spend = useQuery({
    queryKey: ["processing", "spend"],
    refetchInterval: 300_000,
    queryFn: async () => {
      const { data, error } = await apiClient(token).from("processing_spend").select(columns("processing_spend")).limit(50);
      if (error) throw new Error(apiErrorMessage(error));
      return (data ?? []) as ProcessingSpend[];
    },
  });
  const history = useQuery({
    queryKey: ["processing", "history", win],
    refetchInterval: 300_000,
    queryFn: () => callFunction<SeriesRow[]>(token, "processing_series", { p_window: win }),
  });
  /* The scheduled jobs on one shared timeline (engine migration 227). */
  const schedule = useQuery({
    queryKey: ["processing", "schedule", win],
    refetchInterval: 300_000,
    queryFn: () => callFunction<ScheduleRow[]>(token, "processing_schedule", { p_window: win }),
  });
  /* The window's totals, from the engine (migration 179), so the page never
     adds a line up itself. */
  const totals = useQuery({
    queryKey: ["processing", "window", win],
    refetchInterval: 300_000,
    queryFn: () => callFunction<WindowRow[]>(token, "processing_window", { p_window: win }),
  });
  const windowAll = windowTotal(totals.data, null);
  const changes = useQuery({
    queryKey: ["processing", "changes"],
    refetchInterval: 60_000,
    queryFn: async () => {
      const { data, error } = await apiClient(token).from("setting_change").select(columns("setting_change"))
        .order("changed_at", { ascending: false }).order("id", { ascending: false }).limit(50);
      if (error) throw new Error(apiErrorMessage(error));
      return (data ?? []) as SettingChange[];
    },
  });

  const hist = useMemo(() => history.data ?? [], [history.data]);
  const workerByKey = useMemo(() => new Map((workers.data ?? []).map((w) => [w.key ?? "", w])), [workers.data]);
  const stages = STAGES.map((s) => ({ key: s.key, label: workerByKey.get(s.key)?.label ?? s.label, row: workerByKey.get(s.key) }));
  const kindRows = useMemo(() => {
    const rows = (kinds.data ?? []).filter((k) => k.key !== "other");
    return rows.sort((a, b) => kindRank(a.key) - kindRank(b.key));
  }, [kinds.data]);
  const kindLabels = useMemo(() => {
    const m: Record<string, string> = {};
    for (const k of kinds.data ?? []) if (k.key) m[k.key] = k.label ?? k.key;
    return m;
  }, [kinds.data]);
  const gridCell = (stage: string, kind: string) =>
    (grid.data ?? []).find((g) => g.worker === stage && g.work_kind === kind);

  const refreshAll = () => queryClient.invalidateQueries({ queryKey: ["processing"] });
  const anyError = [status, grid, workers, kinds, choices, spend, history, schedule].find((q) => q.isError);
  const queueWorkers = (workers.data ?? []).filter((w) => w.shape === "queue");

  /* The two charts. Spend is hourly for a day and summed into UTC days for a
     week or a month, the way the limit counts. Records added is daily from
     the engine. */
  const spendBuckets = useMemo<Bucket[]>(() => {
    const perKind = kindsIn(hist, "spend").map((k) => {
      const pts = seriesFor(hist, "spend", null, k);
      return { k, pts: win === "24h" ? pts : byDay(pts) };
    });
    const ats = [...new Set(perKind.flatMap((x) => x.pts.map((p) => p.at)))].sort();
    return ats.map((at) => ({
      at,
      parts: perKind.map((x) => ({ key: x.k, value: x.pts.find((p) => p.at === at)?.value ?? 0 })),
    }));
  }, [hist, win]);
  const addedBuckets = useMemo<Bucket[]>(() => {
    const perKind = kindsIn(hist, "added").map((k) => ({ k, pts: seriesFor(hist, "added", null, k) }));
    const ats = [...new Set(perKind.flatMap((x) => x.pts.map((p) => p.at)))].sort();
    return ats.map((at) => ({
      at,
      parts: perKind.map((x) => ({ key: x.k, value: x.pts.find((p) => p.at === at)?.value ?? 0 })),
    }));
  }, [hist]);

  /* One scale for the stage sparklines, so a tall queue and a short one can
     be told apart at a glance rather than both filling their box. */
  const stageTop = useMemo(() => Math.max(1, ...stages.map((s) =>
    Math.max(0, ...seriesFor(hist, "waiting", s.key, null).map((p) => p.value)))), [hist, stages]);

  const s = status.data;
  return (
    <main className="processing">
      <div className="bar">
        <h1 style={{ margin: 0 }}>Processing</h1>
        <span className="muted">{loadedAt(status.dataUpdatedAt)}</span>
        <button onClick={refreshAll} disabled={status.isFetching}>
          {status.isFetching ? <><Spinner />Refreshing</> : "Refresh"}
        </button>
        <span className="window" role="group" aria-label="History window">
          {WINDOWS.map((w) => (
            <button key={w.key} className={w.key === win ? "pressed" : ""} aria-pressed={w.key === win}
                    onClick={() => setWin(w.key)}>{w.label}</button>
          ))}
        </span>
      </div>

      {anyError ? (
        <p className="refusal" role="alert">
          EVIE Sales could not read the engine. {(anyError.error as Error).message} A report was logged.{" "}
          <button className="link" onClick={refreshAll}>Try again</button>
        </p>
      ) : null}

      {status.isLoading ? <p className="muted"><Spinner />Reading the engine.</p> : null}
      {s ? (
        <div className="status-row">
          <p className={s.state === "stopped" ? "refusal status-line" : "status-line"} role="status">
            <strong>{statusSentence(s)}</strong>
            {s.state === "stopped" && s.stopped_by === "service" && s.stopped_reason
              ? <span className="muted"> {s.stopped_reason}</span> : null}
            {s.state === "held" && s.held_by === "service" && s.held_reason ? <span className="muted"> {s.held_reason}</span> : null}
          </p>
          <PauseControl token={token} s={s} onChanged={refreshAll} />
        </div>
      ) : null}

      {s ? (
        <section className="limit">
          <div className="bar">
            <h2 style={{ margin: 0 }}>Model calls today</h2>
            <button className="link" onClick={() => setLimitOpen(true)}>Change the limit ›</button>
          </div>
          {s.cap_usd ? (
            <div className="limit-bar" role="img"
                 aria-label={`${money(s.spent_today)} of ${money(s.cap_usd)}${Number(s.reserve_usd ?? 0) > 0 ? `, ${money(s.reserve_usd)} kept for types set to First` : ""}`}>
              <div style={{ width: `${Math.min(100, (100 * Number(s.spent_today ?? 0)) / Number(s.cap_usd))}%` }} />
              {Number(s.reserve_usd ?? 0) > 0 ? (
                <span className="reserve-mark"
                      style={{ left: `${Math.max(0, 100 - (100 * Number(s.reserve_usd)) / Number(s.cap_usd))}%` }} />
              ) : null}
            </div>
          ) : null}
          <p style={{ margin: "0.2rem 0" }}>{limitSentence(s)}</p>
          <p className="muted" style={{ margin: 0, fontSize: 12 }}>
            {count(s.calls_today)} calls. Tokens in {count(s.input_tokens_today)}, out {count(s.output_tokens_today)},
            cache read {count(s.cache_read_tokens_today)}, cache written {count(s.cache_write_tokens_today)}.
            {s.override_until ? <> Running past the limit until {shortDate(s.override_until)}.</> : null}
          </p>
          <p className="window-total" style={{ margin: "0.5rem 0 0.1rem" }}>
            {windowAll ? (
              <><strong>{money(windowAll.spend)}</strong> over {windowLabel(win)}, on {count(windowAll.calls)} calls, with {count(windowAll.added)} records added.</>
            ) : totals.isError ? (
              <span className="refusal">The totals over the window could not be read. {(totals.error as Error).message}</span>
            ) : (
              <span className="muted">Adding up {windowLabel(win)}.</span>
            )}
          </p>
          <div className="spark-row">
            <span className="muted">Spend an hour, first to last</span>
            <Sparkline points={seriesFor(hist, "spend", null, null)} format={money} width={240} />
          </div>
        </section>
      ) : null}

      <h2>Pages, by who asked and where they are</h2>
      <table className="tight grid">
        <caption className="sr-only">Pages waiting at each stage, by who asked for them</caption>
        <thead>
          <tr>
            <th scope="col"><span className="sr-only">Type of work</span></th>
            {stages.map((st) => <th key={st.key} scope="col" className="num">{st.label}</th>)}
            <th scope="col" className="num">Spend today</th>
            <th scope="col">Priority</th>
            <th scope="col">Spend over the window</th>
          </tr>
        </thead>
        <tbody>
          {kindRows.map((k) => (
            <tr key={k.key}>
              <th scope="row">
                <button className="link" onClick={() => setKindOpen(k.key ?? null)}>{k.label}</button>
                {k.daily_allowance !== null && k.daily_allowance !== undefined
                  ? <div className="muted change">{allowanceWords(k)}</div> : null}
              </th>
              {stages.map((st) => {
                const g = gridCell(st.key, k.key ?? "");
                const waiting = g?.waiting ?? null;
                return (
                  <td key={st.key} className="num cell">
                    {waiting === null ? "" : waiting === 0 ? (
                      <span className="muted" aria-label={`${k.label} at ${st.label}, none waiting`}>·</span>
                    ) : (
                      <button className="link" onClick={() => setCell({ stage: st.key, kind: k.key ?? "" })}
                              aria-label={`${k.label} at ${st.label}, ${count(waiting)} waiting`}>
                        {count(waiting)}
                      </button>
                    )}
                    {g?.change_last_hour ? <div className="muted change">{changeWords(g.change_last_hour)}</div> : null}
                  </td>
                );
              })}
              <td className="num">
                {k.spend_share_today !== null && k.spend_share_today !== undefined ? `${Math.round(Number(k.spend_share_today))}%` : ""}
                <div className="muted change">{money(k.spend_today)}</div>
              </td>
              <td className="priority-cell">
                <PriorityControl token={token} kind={k}
                                 choices={(choices.data ?? []).filter((c) => c.work_kind === k.key)}
                                 resetsAt={s?.resets_at ?? null}
                                 stageStarts={stages.map((st) => st.row?.running_since ?? st.row?.last_started_at)}
                                 onChanged={() => queryClient.invalidateQueries({ queryKey: ["processing"] })} />
              </td>
              <td className="num">
                {(() => {
                  const t = windowTotal(totals.data, k.key ?? null);
                  return (
                    <Sparkline points={seriesFor(hist, "spend", null, k.key ?? null)} format={money} width={96} stacked
                               {...(t ? { summary: money(t.spend) } : {})} />
                  );
                })()}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <FootRow label="Out an hour" stages={stages} value={(w) => w?.items_last_hour === null || w?.items_last_hour === undefined ? "" : count(w.items_last_hour)} />
          <FootRow label="Busy" stages={stages} value={(w) => w?.busy_pct === null || w?.busy_pct === undefined ? "" : `${w.busy_pct}%`} />
          <FootRow label="Clears" stages={stages} value={(w) => clearsWords(w?.clears_state, w?.clears_at)} />
          <FootRow label="Spend today" stages={stages} value={(w) => money(w?.spend_today)} />
          <FootRow label="Last ran" stages={stages} value={(w) => lastRan(w)} />
          <tr>
            <th scope="row">Waiting over the window</th>
            {stages.map((st) => (
              <td key={st.key} className="num">
                <Sparkline points={seriesFor(hist, "waiting", st.key, null)} top={stageTop} width={96} stacked />
              </td>
            ))}
            <td colSpan={3} />
          </tr>
          <FootRow label="Pace" stages={stages} value={(w) => <PaceCell w={w} onOpen={setPaceOpen} />} />
        </tfoot>
      </table>
      <p className="muted" style={{ fontSize: 12 }}>
        Look up counts records. The other columns count pages. A dot is zero. The change under a count is the last hour.
      </p>

      <h2>Other queues</h2>
      <table className="tight">
        <caption className="sr-only">Queues of pairs, companies and jobs</caption>
        <thead>
          <tr>
            <th scope="col">Queue</th><th scope="col" className="num">Waiting</th><th scope="col" className="num">Out an hour</th>
            <th scope="col" className="num">Busy</th><th scope="col">Clears</th><th scope="col">Last ran</th>
            <th scope="col">Waiting over the window</th><th scope="col">Pace</th>
          </tr>
        </thead>
        <tbody>
          {queueWorkers.map((w) => (
            <tr key={w.key}>
              <th scope="row" style={{ fontWeight: 400 }}>{w.label}</th>
              <td className="num">{count(w.waiting)}{w.change_last_hour ? <div className="muted change">{changeWords(w.change_last_hour)}</div> : null}</td>
              <td className="num">{w.items_last_hour === null || w.items_last_hour === undefined ? "" : count(w.items_last_hour)}</td>
              <td className="num">{w.busy_pct === null || w.busy_pct === undefined ? "" : `${w.busy_pct}%`}</td>
              <td>{clearsWords(w.clears_state, w.clears_at)}</td>
              <td>{lastRan(w)}</td>
              <td><Sparkline points={seriesFor(hist, "waiting", w.key ?? null, null)} /></td>
              <td><PaceCell w={w} onOpen={setPaceOpen} /></td>
            </tr>
          ))}
        </tbody>
      </table>

      <h2>Scheduled work</h2>
      <ScheduledWork rows={schedule.data} workers={workerByKey} win={win}
                     failed={schedule.isError ? (schedule.error as Error).message : null}
                     loading={schedule.isLoading} onOpen={setJobOpen} />

      <h2>Spend and yield</h2>
      <table className="tight">
        <caption className="sr-only">Spend and records added, today and the last seven days</caption>
        <thead>
          <tr>
            <th scope="col"><span className="sr-only">Type of work</span></th>
            <th scope="col" className="num">Calls today</th>
            <th scope="col" className="num">Tokens in</th>
            <th scope="col" className="num">Tokens out</th>
            <th scope="col" className="num">Spend today</th>
            <th scope="col" className="num">Added today</th>
            <th scope="col" className="num">Per record</th>
            <th scope="col" className="num">Calls, 7 days</th>
            <th scope="col" className="num">Spend, 7 days</th>
            <th scope="col" className="num">Added, 7 days</th>
            <th scope="col" className="num">Per record, 7 days</th>
          </tr>
        </thead>
        <tbody>
          {(spend.data ?? []).slice().sort((a, b) => kindRank(a.work_kind) - kindRank(b.work_kind)).map((r) => (
            <tr key={r.work_kind}>
              <th scope="row" style={{ fontWeight: 400 }}>{r.label}</th>
              <td className="num">{count(r.calls_today)}</td>
              <td className="num">{count(r.input_tokens_today)}</td>
              <td className="num">{count(r.output_tokens_today)}</td>
              <td className="num">{money(r.spend_today)}</td>
              <td className="num">{count(r.added_today)}</td>
              <td className="num">{cents(r.spend_per_record_today)}</td>
              <td className="num">{count(r.calls_7d)}</td>
              <td className="num">{money(r.spend_7d)}</td>
              <td className="num">{count(r.added_7d)}</td>
              <td className="num">{cents(r.spend_per_record_7d)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <h3>Spend by type{win === "24h" ? ", by hour" : ", by UTC day"}</h3>
      <StackedBars buckets={spendBuckets} labels={kindLabels} format={money} title="Spend by type over the window"
                   cap={win !== "24h" ? s?.cap_usd ?? null : null}
                   labelAt={win === "24h" ? (iso) => localTime(iso) : utcDay} />
      <h3>Records added by type, by UTC day</h3>
      <StackedBars buckets={addedBuckets} labels={kindLabels} format={(n) => Math.round(n).toLocaleString()}
                   title="Records added by type over the window" />

      <h2>Hand edits</h2>
      <EditGrace token={token} onChanged={refreshAll} />

      <h2>Changes</h2>
      {changes.isError ? (
        <p className="refusal" role="alert">EVIE Sales could not read the record of changes: {(changes.error as Error).message}</p>
      ) : changes.isLoading ? (
        <p className="muted"><Spinner />Reading the record of changes.</p>
      ) : !(changes.data ?? []).length ? (
        <p className="muted">No changes recorded yet.</p>
      ) : (
        <>
        {(changes.data ?? []).length >= 50 ? <p className="muted">The newest 50 changes.</p> : null}
        <table className="tight">
          <caption className="sr-only">Changes to the engine&apos;s settings, newest first</caption>
          <thead>
            <tr><th scope="col">When</th><th scope="col">Who</th><th scope="col">What</th><th scope="col">Change</th><th scope="col">Effect</th></tr>
          </thead>
          <tbody>
            {(changes.data ?? []).map((c) => (
              <tr key={c.id}>
                <td>{c.changed_at ? whenWords(c.changed_at) : ""}</td>
                <td>{c.changed_by}</td>
                <td>{c.what}</td>
                <td>{changeFromToWords(c)}</td>
                <td className="muted">{effectWords(c.effect, c.effect_ready_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </>
      )}

      {cell ? (
        <CellPanel token={token} cell={cell} stageLabel={stages.find((st) => st.key === cell.stage)?.label ?? cell.stage}
                   kindLabel={kindLabels[cell.kind] ?? cell.kind} grid={gridCell(cell.stage, cell.kind)}
                   history={seriesFor(hist, "waiting", cell.stage, cell.kind)} onClose={() => setCell(null)} />
      ) : null}
      {kindOpen ? (
        <KindPanel token={token} kind={(kinds.data ?? []).find((k) => k.key === kindOpen)}
                   onClose={() => setKindOpen(null)} onChanged={refreshAll} />
      ) : null}
      {jobOpen && workerByKey.get(jobOpen) ? (
        <JobPanel key={jobOpen} token={token} w={workerByKey.get(jobOpen) as ProcessingWorker} labels={kindLabels}
                  row={(schedule.data ?? []).find((r) => r.worker === jobOpen)} onClose={() => setJobOpen(null)} />
      ) : null}
      {paceOpen && workerByKey.get(paceOpen) ? (
        <PacePanel key={paceOpen} token={token} w={workerByKey.get(paceOpen) as ProcessingWorker} labels={kindLabels}
                   onClose={() => setPaceOpen(null)} onChanged={refreshAll} />
      ) : null}
      {limitOpen && s ? (
        <LimitPanel token={token} s={s} onClose={() => setLimitOpen(false)} onChanged={refreshAll} />
      ) : null}
    </main>
  );
}

/* The one control on the status line: stop every model call, a person's
   included, until resumed or until the limit resets. Asking how long is the
   second step, because this stops everything. Becomes Resume while paused.
   Calls api.set_model_calls_paused (engine migration 171). */
function PauseControl({ token, s, onChanged }: { token: string; s: ProcessingStatus; onChanged: () => void }) {
  const [asking, setAsking] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const set = useMutation({
    mutationFn: (until: string | null) => callFunction(token, "set_model_calls_paused", { p_until: until }),
    onSuccess: () => { setAsking(false); setRefusal(null); onChanged(); },
    onError: (err: Error) => setRefusal(err.message),
  });
  return (
    <div className="pause">
      {s.paused_until ? (
        <button onClick={() => set.mutate(null)} disabled={set.isPending}>
          {set.isPending ? <><Spinner />Resuming</> : "Resume model calls"}
        </button>
      ) : asking ? (
        <span className="pause-ask" role="group" aria-label="Pause all model calls for how long">
          <span>Pause all model calls</span>
          <button onClick={() => set.mutate("infinity")} disabled={set.isPending}>Until I resume them</button>
          <button onClick={() => { if (s.resets_at) set.mutate(s.resets_at); }} disabled={set.isPending || !s.resets_at}>
            Until the limit resets{s.resets_at ? `, ${whenWords(s.resets_at)}` : ""}
          </button>
          <button className="link" onClick={() => setAsking(false)}>Cancel</button>
        </span>
      ) : (
        <button onClick={() => setAsking(true)}>Pause all model calls</button>
      )}
      {refusal ? <p className="refusal" role="alert">{refusal}</p> : null}
    </div>
  );
}

/* The limit's three settings, opened from the limit bar: the daily amount,
   the reserve kept for types set to First, and the time to run past
   the limit until. All three are sent together, as the engine sets them
   (api.set_spend_limit, migration 171), and a refusal shows as written. */
function LimitPanel({ token, s, onClose, onChanged }: {
  token: string; s: ProcessingStatus; onClose: () => void; onChanged: () => void;
}) {
  const [cap, setCap] = useState(s.cap_usd === null || s.cap_usd === undefined ? "" : String(s.cap_usd));
  const [reserve, setReserve] = useState(String(s.reserve_usd ?? 0));
  const [until, setUntil] = useState(localInputValue(s.override_until));
  const [refusal, setRefusal] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const set = useMutation({
    mutationFn: () => callFunction(token, "set_spend_limit", {
      p_cap: cap.trim() === "" ? null : Number(cap),
      p_reserve: reserve.trim() === "" ? 0 : Number(reserve),
      // An override left as it was is sent back exactly. The field holds no
      // seconds, so sending its value would move the override earlier and
      // record a change nobody made.
      p_override_until: until === localInputValue(s.override_until) ? (s.override_until ?? null) : fromLocalInput(until),
    }),
    onSuccess: () => { setRefusal(null); setSaved(`Saved ${localTime(new Date().toISOString())}. The next model call reads it.`); onChanged(); },
    onError: (err: Error) => { setSaved(null); setRefusal(err.message); },
  });
  return (
    <Panel title="The daily limit on model calls" subtitle={limitSentence(s)} onClose={onClose}>
      <form className="panel-section pace-form" noValidate onSubmit={(e) => { e.preventDefault(); set.mutate(); }}>
        <label>
          <span className="field-label">Daily limit, in dollars</span>
          <input type="number" inputMode="decimal" min="1" step="1" value={cap} onChange={(e) => setCap(e.target.value)} />
          {s.cap_max_usd ? <span className="muted help">At most {money(s.cap_max_usd)} from here.</span> : null}
        </label>
        <label>
          <span className="field-label">Keep for types set to First, in dollars</span>
          <input type="number" inputMode="decimal" min="0" step="1" value={reserve} onChange={(e) => setReserve(e.target.value)} />
          <span className="muted help">Bulk work stops here. Sent by a person is always First.</span>
        </label>
        <label>
          <span className="field-label">Run past the limit until</span>
          <span className="input-row">
            <input type="datetime-local" value={until} onChange={(e) => setUntil(e.target.value)} />
            {until ? <button type="button" className="link" onClick={() => setUntil("")}>Clear</button> : null}
          </span>
          <span className="muted help">Empty means the limit applies. At most 7 days ahead.</span>
        </label>
        <div className="bar">
          <button type="submit" disabled={set.isPending}>{set.isPending ? <><Spinner />Saving</> : "Save"}</button>
          {saved ? <span className="muted" role="status">{saved}</span> : null}
        </div>
        {refusal ? <p className="refusal" role="alert">{refusal}</p> : null}
      </form>
      <ExtractFirst token={token} s={s} onChanged={onChanged} />
    </Panel>
  );
}

/* Extract first (engine migrations 174 and 177): when the rest of today's
   limit cannot cover what Extract can use before the reset, Triage takes only
   the pages of First types, Sent by a person among them, so the money goes to
   finishing pages. A switch, with the
   engine's sentence on where the rule stands now. */
function ExtractFirst({ token, s, onChanged }: { token: string; s: ProcessingStatus; onChanged: () => void }) {
  const [refusal, setRefusal] = useState<string | null>(null);
  const set = useMutation({
    mutationFn: (on: boolean) => callFunction(token, "set_extract_first", { p_on: on }),
    onSuccess: () => { setRefusal(null); onChanged(); },
    onError: (err: Error) => setRefusal(err.message),
  });
  const on = !!s.extract_first;
  return (
    <section className="panel-section" aria-labelledby="extract-first-h">
      <h3 id="extract-first-h">Extract first</h3>
      <p>Triage waits when today&apos;s limit cannot cover both it and Extract.</p>
      {s.extract_first_reason ? <p className="muted">{s.extract_first_reason}</p> : null}
      <div className="bar">
        <span>{on ? "On." : "Off."}</span>
        <button onClick={() => set.mutate(!on)} disabled={set.isPending}>
          {set.isPending ? <><Spinner />Saving</> : on ? "Turn off" : "Turn on"}
        </button>
      </div>
      {refusal ? <p className="refusal" role="alert">{refusal}</p> : null}
    </section>
  );
}

/* A worker's pace, opened from its pace words under a stage's column or at
   the end of a queue's row: items a pass, how often, and how many at once,
   each beside what the worker measured, a pause switch, and its last 24
   passes. Only the parts a person changed are sent (paceChanges), and the
   engine refuses a value outside its bounds with the reason
   (api.set_worker_pace, migration 170). A pass already running finishes on
   the pace it started with. */
function PacePanel({ token, w, labels, onClose, onChanged }: {
  token: string; w: ProcessingWorker; labels: Record<string, string>; onClose: () => void; onChanged: () => void;
}) {
  const passes = useQuery({
    queryKey: ["processing", "passes", w.key],
    queryFn: () => callFunction<PassRow[]>(token, "processing_passes", { p_worker: w.key }),
  });
  const [batch, setBatch] = useState(String(w.batch ?? ""));
  const [every, setEvery] = useState(w.every_seconds ? String(w.every_seconds / 60) : "");
  const [conc, setConc] = useState(String(w.concurrency ?? 1));
  const [paused, setPaused] = useState(!!w.paused);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const change = paceChanges(w, { batch, everyMinutes: every, concurrency: conc, paused });
  const nothing = Object.values(change).every((v) => v === null);
  const set = useMutation({
    mutationFn: () => callFunction(token, "set_worker_pace", { p_worker: w.key, ...change }),
    onSuccess: () => { setRefusal(null); setSaved(`Saved ${localTime(new Date().toISOString())}. The next pass takes it.`); onChanged(); },
    onError: (err: Error) => { setSaved(null); setRefusal(err.message); },
  });
  const items = w.items_word ?? "items";
  const fixedThreads = (w.concurrency_max ?? 1) <= 1;
  return (
    <Panel title={`${w.label ?? w.key}: pace`}
           subtitle={w.paused ? "Paused. No pass runs until it is resumed."
             : w.next_pass_at ? `${paceWords(w)}. Next pass about ${localTime(w.next_pass_at)}.` : paceWords(w)}
           onClose={onClose}>
      <form className="panel-section pace-form" noValidate onSubmit={(e) => { e.preventDefault(); if (!nothing) set.mutate(); }}>
        <label>
          <span className="field-label">{items.charAt(0).toUpperCase() + items.slice(1)} a pass</span>
          <input type="number" inputMode="numeric" min={w.batch_min ?? 1} max={w.batch_max ?? undefined} step="1"
                 value={batch} onChange={(e) => setBatch(e.target.value)} />
          <span className="muted help">
            {w.batch_min ?? 1} to {w.batch_max?.toLocaleString() ?? "any"}.
            {w.last_taken !== null && w.last_taken !== undefined ? ` The last pass took ${w.last_taken.toLocaleString()}.` : ""}
          </span>
        </label>
        <label>
          <span className="field-label">Run every, in minutes</span>
          {w.pace_fixed ? (
            <span>{paceWords({ every_seconds: w.every_seconds ?? null, shape: "scheduled" })}</span>
          ) : (
            <input type="number" inputMode="decimal" min={(w.every_min_seconds ?? 60) / 60} step="1"
                   value={every} onChange={(e) => setEvery(e.target.value)} />
          )}
          <span className="muted help">
            {w.pace_fixed ? w.bounds_note : `At least ${Math.ceil((w.every_min_seconds ?? 60) / 60)} minutes apart.`}
            {w.running_since ? ` A pass has been running since ${localTime(w.running_since)}.`
              : w.last_started_at ? ` Last ran ${agoWords(w.last_started_at)}.` : ""}
          </span>
        </label>
        <label>
          <span className="field-label">At once</span>
          {fixedThreads ? <span>One at a time</span> : (
            <input type="number" inputMode="numeric" min="1" max={w.concurrency_max ?? undefined} step="1"
                   value={conc} onChange={(e) => setConc(e.target.value)} />
          )}
          <span className="muted help">
            {fixedThreads ? w.bounds_note : `1 to ${w.concurrency_max}. ${w.bounds_note ?? ""}`}
            {w.busy_pct !== null && w.busy_pct !== undefined ? ` The last pass used ${w.busy_pct}% of its slot.` : ""}
          </span>
        </label>
        {w.pace_fixed ? null : (
          <label className="check">
            <input type="checkbox" checked={paused} onChange={(e) => setPaused(e.target.checked)} />
            <span>Pause this {w.shape === "stage" ? "stage" : "queue"}</span>
          </label>
        )}
        <div className="bar">
          <button type="submit" disabled={set.isPending || nothing}>{set.isPending ? <><Spinner />Saving</> : "Save"}</button>
          {saved ? <span className="muted" role="status">{saved}</span> : null}
        </div>
        {refusal ? <p className="refusal" role="alert">{refusal}</p> : null}
      </form>
      {w.key === "lookup" ? <LookupWindows token={token} onChanged={onChanged} /> : null}
      {w.key === "review" ? <ReviewSwitches token={token} onChanged={onChanged} /> : null}
      <div className="panel-section">
        <h3 style={{ margin: "0 0 0.3rem" }}>Its last passes</h3>
        <PassTable passes={passes} w={w} labels={labels} />
      </div>
    </Panel>
  );
}

/* A worker's pace as the footer and the queue rows show it: the words, as the
   button that opens the Pace panel, and the change waiting for its next pass. */
function PaceCell({ w, onOpen }: { w: ProcessingWorker | undefined; onOpen: (key: string) => void }) {
  if (!w) return null;
  const waiting = changeWaiting(w.pace_set_at, [w.running_since ?? w.last_started_at]);
  return (
    <>
      <button className="link" onClick={() => onOpen(w.key ?? "")}>{w.paused ? "Paused" : paceWords(w)}</button>
      {waiting ? <div className="muted change">Changed {w.pace_set_at ? localTime(w.pace_set_at) : ""}. Takes effect at the next pass.</div> : null}
    </>
  );
}

/* A kind's priority and the menu that changes it, at the end of its row
   beside its share of the spend (docs/plans/processing-screen.md, "The
   Priority control"). Five steps. Low, Normal and High apply until changed.
   Paused and First ask whether they last until changed or until the limit
   resets, because those two are the ones a person usually means for a while.
   The sentence under each step and every refusal are the engine's. Sent by a
   person is always First and has no menu. */
function PriorityControl({ token, kind, choices, resetsAt, stageStarts, onChanged }: {
  token: string;
  kind: ProcessingKind;
  choices: ProcessingPriorityChoice[];
  resetsAt: string | null;
  stageStarts: (string | null | undefined)[];
  onChanged: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [asking, setAsking] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const wrap = useRef<HTMLDivElement | null>(null);
  const set = useMutation({
    mutationFn: (v: { priority: string; until: string | null }) =>
      callFunction(token, "set_kind_priority", { p_kind: kind.key, p_priority: v.priority, p_until: v.until }),
    onSuccess: () => { setOpen(false); setAsking(null); setRefusal(null); onChanged(); },
    onError: (err: Error) => setRefusal(err.message),
  });

  useEffect(() => {
    if (!open) return undefined;
    const away = (e: MouseEvent) => { if (wrap.current && !wrap.current.contains(e.target as Node)) close(); };
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") close(); };
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", away); document.removeEventListener("keydown", esc); };
  }, [open]);

  function close() { setOpen(false); setAsking(null); setRefusal(null); }
  function pick(priority: string) {
    if (priority === "paused" || priority === "first") { setAsking(priority); return; }
    set.mutate({ priority, until: null });
  }

  const words = priorityWords(kind.priority, kind.priority_until, kind.priority_after);
  const waiting = changeWaiting(kind.priority_set_at, stageStarts);
  return (
    <div className="priority" ref={wrap}>
      {kind.key === "person" ? <span>{words}</span> : (
        <button className="menu-button" aria-haspopup="true" aria-expanded={open}
                onClick={() => (open ? close() : setOpen(true))}>
          {words} <span aria-hidden="true">▾</span>
        </button>
      )}
      {waiting && kind.key !== "person" ? (
        <div className="muted change">
          Changed {kind.priority_set_at ? localTime(kind.priority_set_at) : ""}
          {kind.priority_set_by ? ` by ${kind.priority_set_by}` : ""}. Each stage takes it from its next pass.
        </div>
      ) : null}
      {open ? (
        <div className="menu" role="menu" aria-label={`Priority for ${kind.label ?? ""}`}>
          {asking ? (
            <div className="menu-ask">
              <p>{PRIORITY_WORDS[asking]} for how long?</p>
              <button onClick={() => set.mutate({ priority: asking, until: null })} disabled={set.isPending}>
                Until I change it
              </button>
              <button onClick={() => set.mutate({ priority: asking, until: resetsAt })}
                      disabled={set.isPending || !resetsAt}>
                Until the limit resets{resetsAt ? `, ${whenWords(resetsAt)}` : ""}
              </button>
              <button className="link" onClick={() => setAsking(null)}>Back</button>
            </div>
          ) : choices.map((c) => (
            <button key={c.priority} role="menuitemradio" aria-checked={!!c.current}
                    className={c.current ? "menu-item current" : "menu-item"}
                    disabled={set.isPending} onClick={() => pick(c.priority ?? "")}>
              <strong>{c.label}{c.current ? " (now)" : ""}</strong>
              <span className="muted">{c.sentence}</span>
            </button>
          ))}
          {set.isPending ? <p className="muted"><Spinner />Saving</p> : null}
          {refusal ? <p className="refusal" role="alert">{refusal}</p> : null}
        </div>
      ) : null}
    </div>
  );
}

/* When a worker last ran, or that it is running now, or that its unit is
   failing, which links to the alert rather than repeating it. */
function lastRan(w: ProcessingWorker | undefined): ReactNode {
  if (!w) return "";
  if (w.open_event_id) return <Link to="/alerts" className="refusal">Failing, alert {w.open_event_id}</Link>;
  if (w.running_since) return `Running for ${durationWords(w.running_since, new Date().toISOString()) || "under a minute"}`;
  if (w.last_started_at) return agoWords(w.last_started_at);
  return <span className="muted">No pass recorded yet</span>;
}

function FootRow({ label, stages, value }: {
  label: string;
  stages: { key: string; row: ProcessingWorker | undefined }[];
  value: (w: ProcessingWorker | undefined) => ReactNode;
}) {
  return (
    <tr>
      <th scope="row">{label}</th>
      {stages.map((st) => <td key={st.key} className="num">{value(st.row)}</td>)}
      <td colSpan={3} />
    </tr>
  );
}

/* A cell opened: what is in it, by page kind, and ten of its items at random,
   so a person can see what the work is without a query. */
function CellPanel({ token, cell, stageLabel, kindLabel, grid, history, onClose }: {
  token: string; cell: Cell; stageLabel: string; kindLabel: string;
  grid: ProcessingGrid | undefined; history: { at: string; value: number }[]; onClose: () => void;
}) {
  const sample = useQuery({
    queryKey: ["processing", "sample", cell.stage, cell.kind],
    queryFn: () => callFunction<SampleRow[]>(token, "processing_sample",
      { p_worker: cell.stage, p_work_kind: cell.kind }),
  });
  const rows = sample.data ?? [];
  const byKind = new Map<string, number>();
  for (const r of rows) byKind.set(r.page_kind ?? "page", (byKind.get(r.page_kind ?? "page") ?? 0) + 1);
  return (
    <Panel title={`${kindLabel} at ${stageLabel}`}
           subtitle={grid ? <>{count(grid.waiting)} waiting{grid.change_last_hour !== null && grid.change_last_hour !== undefined ? <>, {changeSentence(grid.change_last_hour)}</> : null}
             {grid.oldest_at ? <>. Oldest arrived {agoWords(grid.oldest_at)}</> : null}</> : null}
           onClose={onClose}>
      <div className="panel-section">
        <div className="muted" style={{ fontSize: 12 }}>Waiting over the window</div>
        <Sparkline points={history} width={240} height={32} />
      </div>
      <div className="panel-section">
        <div className="bar">
          <h3 style={{ margin: 0 }}>Ten at random</h3>
          <button onClick={() => sample.refetch()} disabled={sample.isFetching}>
            {sample.isFetching ? <><Spinner />Drawing</> : "Draw again"}
          </button>
          {byKind.size ? <span className="muted">{[...byKind.entries()].map(([k, n]) => `${k.replace(/_/g, " ")} ${n}`).join(", ")}</span> : null}
        </div>
        {sample.isError ? <p className="refusal" role="alert">{(sample.error as Error).message}</p> : null}
        {sample.isLoading ? <p className="muted"><Spinner />Drawing ten.</p> : null}
        {!sample.isLoading && rows.length === 0 ? <p className="muted">No items are waiting here.</p> : null}
        {rows.length ? (
          <table className="tight">
            <thead><tr><th>Item</th><th>About</th><th>Arrived</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td>
                    {r.url ? <a href={r.url} target="_blank" rel="noreferrer">{r.title || r.url}</a> : (r.title || r.id)}
                    {r.reason ? <div className="muted" style={{ fontSize: 12 }}>{r.reason}</div> : null}
                  </td>
                  <td><About row={r} /></td>
                  <td>{agoWords(r.arrived_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </div>
    </Panel>
  );
}

/* The record a waiting item is about, as a link where the record has a page.
   A project opens from its property, so it is named and not linked here, and
   a market search is about a place rather than a record. */
const RECORD_ROUTES: Record<string, "companies" | "people" | "properties"> = {
  company: "companies", person: "people", property: "properties",
};
function About({ row }: { row: SampleRow }) {
  const route = RECORD_ROUTES[row.about_type ?? ""];
  if (route) {
    return <><RecordLink to={route} id={row.about_id} label={row.about_label} /> <span className="muted">({row.about_type})</span></>;
  }
  if (row.about_type === "project") return <>{row.about_label ?? ""} <span className="muted">(project)</span></>;
  if (row.about_type === "campaign") return <>{row.about_label ?? ""} <span className="muted">(market search)</span></>;
  return <RecordLink to="companies" id={null} label={null} />;
}

/* A kind opened: what it is, what waits for it and what it cost today. The
   depth settings and the priority read as they are until the controls land. */
/* The page's on or off settings (api.processing_switch, engine migration
   175), read where a panel needs them. */
function useSwitches(token: string) {
  return useQuery({
    queryKey: ["processing", "switches"],
    queryFn: async () => {
      const { data, error } = await apiClient(token).from("processing_switch").select(columns("processing_switch"))
        .order("sort_order").limit(50);
      if (error) throw new Error(apiErrorMessage(error));
      return (data ?? []) as ProcessingSwitch[];
    },
  });
}

/* One on or off setting with who set it last. The button calls the switch's
   setter: the duplicate reviewer's four by the type of record in their key,
   and the new company switch. A refusal shows as the engine wrote it. */
function SwitchRow({ token, sw, onChanged }: {
  token: string; sw: ProcessingSwitch; onChanged: () => void;
}) {
  const [refusal, setRefusal] = useState<string | null>(null);
  const set = useMutation({
    mutationFn: (on: boolean) => sw.key?.startsWith("review.decide.")
      ? callFunction(token, "set_review_decides", { p_kind: sw.key.slice("review.decide.".length), p_on: on })
      : sw.key === "enrichment.new_company_lookups"
        ? callFunction(token, "set_new_company_lookups", { p_on: on })
        : Promise.reject(new Error(`The page has no setter for the switch ${sw.key}.`)),
    onSuccess: () => { setRefusal(null); onChanged(); },
    onError: (err: Error) => setRefusal(err.message),
  });
  const on = !!sw.on_now;
  return (
    <div className="switch-row">
      <span>{sw.label}</span>
      <span className="muted">{on ? "On" : "Off"}{sw.set_by ? `, set by ${sw.set_by}` : ""}</span>
      <button onClick={() => set.mutate(!on)} disabled={set.isPending}>
        {set.isPending ? <><Spinner />Saving</> : on ? "Turn off" : "Turn on"}
      </button>
      {refusal ? <p className="refusal" role="alert">{refusal}</p> : null}
    </div>
  );
}

/* A type's lookup depth: how many of its query shapes a record gets, in
   order, and how many results each asks for (api.set_lookup_depth, engine
   migration 175). Both are sent, and the engine refuses a value outside its
   bounds with the reason. */
function DepthForm({ token, kind, onChanged }: { token: string; kind: ProcessingKind; onChanged: () => void }) {
  const [searches, setSearches] = useState(String(kind.searches_per_record ?? ""));
  const [results, setResults] = useState(String(kind.results_per_search ?? ""));
  const [refusal, setRefusal] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const set = useMutation({
    mutationFn: () => callFunction(token, "set_lookup_depth", {
      p_kind: kind.key,
      p_searches: searches.trim() === "" ? null : Number(searches),
      p_results: results.trim() === "" ? null : Number(results),
    }),
    onSuccess: () => { setRefusal(null); setSaved(`Saved ${localTime(new Date().toISOString())}. The next lookup reads it.`); onChanged(); },
    onError: (err: Error) => { setSaved(null); setRefusal(err.message); },
  });
  return (
    <form className="panel-section pace-form" noValidate onSubmit={(e) => { e.preventDefault(); set.mutate(); }}>
      <h3 style={{ margin: 0 }}>Lookup depth</h3>
      <label>
        <span className="field-label">Searches a record</span>
        <input type="number" inputMode="numeric" min="1" max="4" step="1" value={searches} onChange={(e) => setSearches(e.target.value)} />
        <span className="muted help">1 to 4.</span>
      </label>
      <label>
        <span className="field-label">Results a search</span>
        <input type="number" inputMode="numeric" min="1" max="10" step="1" value={results} onChange={(e) => setResults(e.target.value)} />
        <span className="muted help">1 to 10.</span>
      </label>
      <div className="bar">
        <button type="submit" disabled={set.isPending}>{set.isPending ? <><Spinner />Saving</> : "Save"}</button>
        {saved ? <span className="muted" role="status">{saved}</span> : null}
      </div>
      {refusal ? <p className="refusal" role="alert">{refusal}</p> : null}
    </form>
  );
}

/* A type's daily allowance: how many records may be queued for a web lookup
   under it in a UTC day (api.set_kind_allowance, engine migrations 263 and
   273): the sweep of properties known only by name, and the duplicate
   reviewer's lookups. Only such a type has one, and the engine refuses any
   other. */
function allowanceWords(kind: ProcessingKind): string {
  return `${count(kind.queued_today ?? 0)} of ${count(kind.daily_allowance)} queued today`;
}

function AllowanceForm({ token, kind, onChanged }: { token: string; kind: ProcessingKind; onChanged: () => void }) {
  const [value, setValue] = useState(String(kind.daily_allowance ?? ""));
  const [refusal, setRefusal] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const set = useMutation({
    mutationFn: () => callFunction(token, "set_kind_allowance", {
      p_kind: kind.key,
      p_per_day: value.trim() === "" ? null : Number(value),
    }),
    onSuccess: () => { setRefusal(null); setSaved(`Saved ${localTime(new Date().toISOString())}. EVIE reads it on its next pass.`); onChanged(); },
    onError: (err: Error) => { setSaved(null); setRefusal(err.message); },
  });
  return (
    <form className="panel-section pace-form" noValidate onSubmit={(e) => { e.preventDefault(); set.mutate(); }}>
      <h3 style={{ margin: 0 }}>Daily allowance</h3>
      <label>
        <span className="field-label">Records a day</span>
        <input type="number" inputMode="numeric" min="0" max="10000" step="1" value={value} onChange={(e) => setValue(e.target.value)} />
        <span className="muted help">Lookups EVIE may queue a day. 0 stops them.</span>
      </label>
      <div className="bar">
        <button type="submit" disabled={set.isPending}>{set.isPending ? <><Spinner />Saving</> : "Save"}</button>
        {saved ? <span className="muted" role="status">{saved}</span> : null}
      </div>
      {refusal ? <p className="refusal" role="alert">{refusal}</p> : null}
    </form>
  );
}

/* How long a person's edit is kept against sources that are not
   authoritative (api.edit_grace_days and api.set_edit_grace, engine
   migration 187). An authoritative source with a newer dated claim replaces
   it at any time, which the help says in a rep's words. The form mounts once
   the value is read, so it opens on the value the engine holds. */
function EditGrace({ token, onChanged }: { token: string; onChanged: () => void }) {
  const q = useQuery({
    queryKey: ["processing", "edit-grace"],
    queryFn: async () => {
      const { data, error } = await apiClient(token).rpc("edit_grace_days", {}, { get: true }).limit(1);
      if (error) throw new Error(apiErrorMessage(error));
      return data as unknown as number;
    },
  });
  if (q.isError) return <p className="refusal" role="alert">{(q.error as Error).message}</p>;
  if (q.isLoading || q.data == null) return <p className="muted"><Spinner />Reading the setting.</p>;
  return <EditGraceForm key={q.data} token={token} days={q.data} onChanged={() => { void q.refetch(); onChanged(); }} />;
}

function EditGraceForm({ token, days, onChanged }: { token: string; days: number; onChanged: () => void }) {
  const [value, setValue] = useState(String(days));
  const [refusal, setRefusal] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const set = useMutation({
    mutationFn: () => callFunction(token, "set_edit_grace", { p_days: value.trim() === "" ? null : Number(value) }),
    onSuccess: () => { setRefusal(null); setSaved(`Saved ${localTime(new Date().toISOString())}. The next pass reads it.`); onChanged(); },
    onError: (err: Error) => { setSaved(null); setRefusal(err.message); },
  });
  return (
    <form className="panel-section pace-form" noValidate onSubmit={(e) => { e.preventDefault(); set.mutate(); }}>
      <label>
        <span className="field-label">Days a person&apos;s edit is kept</span>
        <input type="number" inputMode="numeric" min="1" max="3650" step="1" value={value} onChange={(e) => setValue(e.target.value)} />
        <span className="muted help">1 to 3650. A newer official source can still replace it.</span>
      </label>
      <div className="bar">
        <button type="submit" disabled={set.isPending}>{set.isPending ? <><Spinner />Saving</> : "Save"}</button>
        {saved ? <span className="muted" role="status">{saved}</span> : null}
      </div>
      {refusal ? <p className="refusal" role="alert">{refusal}</p> : null}
    </form>
  );
}

/* How long a record waits before it is looked up again, by how its last
   lookup ended (api.processing_lookup_window and api.set_lookup_window,
   engine migration 175). One row a window, each with the waits it makes. */
function LookupWindows({ token, onChanged }: { token: string; onChanged: () => void }) {
  const q = useQuery({
    queryKey: ["processing", "lookup-windows"],
    queryFn: async () => {
      const { data, error } = await apiClient(token).from("processing_lookup_window")
        .select(columns("processing_lookup_window")).order("sort_order").limit(10);
      if (error) throw new Error(apiErrorMessage(error));
      return (data ?? []) as ProcessingLookupWindow[];
    },
  });
  return (
    <div className="panel-section">
      <h3 style={{ margin: "0 0 0.3rem" }}>Look a record up again</h3>
      {q.isError ? <p className="refusal">{(q.error as Error).message}</p>
        : q.isLoading ? <p className="muted"><Spinner />Reading the lookup windows.</p>
        : (q.data ?? []).map((lw) => (
          <WindowForm key={`${lw.outcome}-${lw.recheck_days}-${lw.backoff_factor}-${lw.max_days}`}
                      token={token} lw={lw} onChanged={() => { void q.refetch(); onChanged(); }} />
        ))}
    </div>
  );
}

function WindowForm({ token, lw, onChanged }: { token: string; lw: ProcessingLookupWindow; onChanged: () => void }) {
  const [first, setFirst] = useState(String(lw.recheck_days ?? ""));
  const [factor, setFactor] = useState(String(lw.backoff_factor ?? ""));
  const [longest, setLongest] = useState(String(lw.max_days ?? ""));
  const [refusal, setRefusal] = useState<string | null>(null);
  const set = useMutation({
    mutationFn: () => callFunction(token, "set_lookup_window", {
      p_outcome: lw.outcome,
      p_recheck_days: first.trim() === "" ? null : Number(first),
      p_backoff: factor.trim() === "" ? null : Number(factor),
      p_max_days: longest.trim() === "" ? null : Number(longest),
    }),
    onSuccess: () => { setRefusal(null); onChanged(); },
    onError: (err: Error) => setRefusal(err.message),
  });
  return (
    <form className="pace-form window-form" noValidate onSubmit={(e) => { e.preventDefault(); set.mutate(); }}>
      <div className="field-label">{lw.label}</div>
      <div className="input-row">
        <label>First wait, days <input type="number" inputMode="numeric" min="1" step="1" value={first} onChange={(e) => setFirst(e.target.value)} /></label>
        <label>Each wait times <input type="number" inputMode="decimal" min="1" step="0.5" value={factor} onChange={(e) => setFactor(e.target.value)} /></label>
        <label>Longest wait, days <input type="number" inputMode="numeric" min="1" step="1" value={longest} onChange={(e) => setLongest(e.target.value)} /></label>
        <button type="submit" disabled={set.isPending}>{set.isPending ? <><Spinner />Saving</> : "Save"}</button>
      </div>
      <span className="muted help">Waits {lw.waits}</span>
      {refusal ? <p className="refusal" role="alert">{refusal}</p> : null}
    </form>
  );
}

/* The duplicate reviewer's four switches, in its Pace panel: whether it
   decides each type of pair on its own (engine migration 175). */
function ReviewSwitches({ token, onChanged }: { token: string; onChanged: () => void }) {
  const switches = useSwitches(token);
  const mine = (switches.data ?? []).filter((sw) => sw.applies_to === "review");
  return (
    <div className="panel-section">
      <h3 style={{ margin: "0 0 0.3rem" }}>Deciding on its own</h3>
      {switches.isError ? <p className="refusal">{(switches.error as Error).message}</p>
        : switches.isLoading ? <p className="muted"><Spinner />Reading the switches.</p>
        : mine.map((sw) => <SwitchRow key={sw.key} token={token} sw={sw} onChanged={onChanged} />)}
    </div>
  );
}

/* Scheduled work (James, 2026-10-12: the old section was hard to parse).
   One sentence on top, then problems first and the rest from the most
   frequent to the least. Every row has the same cells on one timeline, so a
   moment that failed several jobs reads as one column, and health is one
   word at the end. A job's name opens its last runs. The engine decides
   health, lateness and why a job is off (api.processing_schedule). */
function ScheduledWork({ rows, workers, win, failed, loading, onOpen }: {
  rows: ScheduleRow[] | undefined; workers: Map<string, ProcessingWorker>; win: Window;
  failed: string | null; loading: boolean; onOpen: (key: string) => void;
}) {
  if (failed) return <p className="refusal" role="alert">The scheduled jobs could not be read: {failed}</p>;
  if (loading || !rows) return <p className="muted"><Spinner />Reading the scheduled jobs.</p>;
  if (!rows.length) return <p className="muted">No scheduled jobs.</p>;
  const sorted = [...rows].sort(scheduleOrder);
  const problems = sorted.filter(needsALook);
  const rest = sorted.filter((r) => !needsALook(r));
  const hourly = (rows[0]?.bucket_seconds ?? 86400) === 3600;
  const cells = rows[0]?.buckets ?? [];
  const first = cells[0]?.[0], last = cells[cells.length - 1]?.[0];
  const span = hourly ? "Last 24 hours, by hour"
    : first && last ? `${utcDay(first)} to ${utcDay(last)}, by UTC day` : "";
  const row = (r: ScheduleRow) => {
    const w = workers.get(r.worker ?? "");
    return (
      <tr key={r.worker}>
        <th scope="row" className="sched-job">
          <button className="link" onClick={() => onOpen(r.worker ?? "")}>{r.label ?? r.worker}</button>
          {r.about ? <div className="muted sched-about">{r.about}</div> : null}
        </th>
        <td className="sched-nowrap sched-every">{capital(everyWords(r.every_seconds))}</td>
        {r.health === "off" ? (
          <td colSpan={2} className="muted sched-off">{r.off_reason}</td>
        ) : (
          <>
            <td className="sched-nowrap sched-last">{w?.running_since ? "Running now" : w ? lastRan(w) : ""}</td>
            <td className="sched-nowrap sched-took">{w ? durationWords(w.last_started_at, w.last_finished_at) : ""}</td>
          </>
        )}
        <td className="sched-when">
          <span className="sched-tl" aria-hidden="true" style={{ gridTemplateColumns: `repeat(${(r.buckets ?? []).length || 1}, 1fr)` }}>
            {(r.buckets ?? []).map((c) => <span key={c[0]} className={`sched-cell tl-${c[1]}`} title={cellWords(c, hourly)} />)}
          </span>
          <span className="sr-only">{(r.buckets ?? []).map((c) => cellWords(c, hourly)).join(". ")}</span>
        </td>
        <td className={`sched-nowrap sched-health ${r.health === "failing" || r.health === "late" ? "refusal" : r.health === "ok" || r.health === "off" ? "muted" : ""}`}>
          {r.health === "failing" && w?.open_event_id
            ? <Link to="/alerts" className="refusal">Failing, alert {w.open_event_id}</Link>
            : healthWords(r)}
        </td>
      </tr>
    );
  };
  return (
    <>
      <p>{scheduleSummary(rows, windowLabel(win))}</p>
      <table className="tight sched">
        <caption className="sr-only">Scheduled jobs, problems first, with each one&apos;s runs over {windowLabel(win)}</caption>
        <thead>
          <tr>
            <th scope="col">Job</th><th scope="col">Schedule</th><th scope="col">Last run</th><th scope="col">Took</th>
            <th scope="col">{span}</th><th scope="col">Health</th>
          </tr>
        </thead>
        {problems.length ? (
          <tbody>
            <tr className="sched-group"><th colSpan={6} scope="colgroup">Needs a look</th></tr>
            {problems.map(row)}
          </tbody>
        ) : null}
        <tbody>
          {problems.length ? <tr className="sched-group"><th colSpan={6} scope="colgroup">Running as expected</th></tr> : null}
          {rest.map(row)}
        </tbody>
      </table>
      <p className="muted sched-legend" aria-hidden="true">
        {(["work", "nothing", "failed", "missed", "none"] as const).map((k) => (
          <span key={k}><span className={`sched-cell tl-${k}`} />{k === "work" ? "ran" : CELL_WORDS[k]}</span>
        ))}
      </p>
    </>
  );
}

function capital(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

/* A scheduled job's last runs, opened from its name: what it does, its
   health, and each run with what came of it. */
function JobPanel({ token, w, row, labels, onClose }: {
  token: string; w: ProcessingWorker; row: ScheduleRow | undefined; labels: Record<string, string>; onClose: () => void;
}) {
  const passes = useQuery({
    queryKey: ["processing", "passes", w.key],
    queryFn: () => callFunction<PassRow[]>(token, "processing_passes", { p_worker: w.key }),
  });
  return (
    <Panel title={row?.label ?? w.label ?? w.key} subtitle={row?.about ?? undefined} onClose={onClose}>
      <div className="panel-section">
        <p>{capital(everyWords(row?.every_seconds ?? w.every_seconds))}. {row ? healthWords(row) : ""}.{row?.off_reason ? ` ${row.off_reason}.` : ""}</p>
        <h3 style={{ margin: "0 0 0.3rem" }}>Its last runs</h3>
        <PassTable passes={passes} w={w} labels={labels} job />
      </div>
    </Panel>
  );
}

/* A worker's last passes, newest first, with what came of each, for the
   Pace panel and a scheduled job's panel. A scheduled job takes no items and
   runs one at a time, so its table leaves those two columns out. */
function PassTable({ passes, w, labels, job = false }: {
  passes: { data?: PassRow[] | undefined; isError: boolean; isLoading: boolean; error: unknown };
  w: ProcessingWorker; labels: Record<string, string>; job?: boolean;
}) {
  const rows = passes.data ?? [];
  return (
    <>
      {passes.isError ? <p className="refusal" role="alert">{(passes.error as Error).message}</p> : null}
      {passes.isLoading ? <p className="muted"><Spinner />Reading its passes.</p> : null}
      {!passes.isLoading && !passes.isError && rows.length === 0 ? <p className="muted">No pass recorded yet.</p> : null}
      {rows.length ? (
        <table className="tight">
          <thead><tr><th>Started</th><th>Took</th>
            {job ? null : <><th className="num">Taken</th><th className="num">At once</th></>}
            <th>{job ? "What happened" : "What came of them"}</th></tr></thead>
          <tbody>
            {rows.map((p, i) => (
              <tr key={p.started_at ?? ""}>
                <td>{p.started_at ? localTime(p.started_at) : ""}</td>
                <td>{p.finished_at ? durationWords(p.started_at, p.finished_at)
                  : i === 0 && w.running_since ? "Running" : "Did not finish"}</td>
                {job ? null : <>
                  <td className="num">{p.taken === null ? "" : p.taken.toLocaleString()}</td>
                  <td className="num">{p.concurrency ?? ""}</td>
                </>}
                <td>
                  <span className="muted">{outcomeWords(p.outcomes)}</span>
                  {kindMixWords(p.outcomes, labels) ? <div className="muted change">{kindMixWords(p.outcomes, labels)}</div> : null}
                  {p.error ? <div className="refusal">{p.error}</div> : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </>
  );
}

function KindPanel({ token, kind, onClose, onChanged }: {
  token: string; kind: ProcessingKind | undefined; onClose: () => void; onChanged: () => void;
}) {
  const switches = useSwitches(token);
  if (!kind) return null;
  const reasons = reasonLines(kind.lookup_reasons);
  const mine = (switches.data ?? []).filter((sw) => sw.applies_to === kind.key);
  return (
    <Panel title={kind.label ?? ""} subtitle={kind.sentence} onClose={onClose}>
      <div className="panel-section">
        <div className="fields">
          <div><div className="field-label">Priority</div>{priorityWords(kind.priority, kind.priority_until, kind.priority_after)}</div>
          <div><div className="field-label">Pages waiting</div>{count(kind.pages_waiting)}</div>
          <div><div className="field-label">Lookups queued</div>{count(kind.lookups_queued)}</div>
          <div><div className="field-label">Calls today</div>{count(kind.calls_today)}</div>
          <div><div className="field-label">Spend today</div>{money(kind.spend_today)}{kind.spend_share_today !== null && kind.spend_share_today !== undefined ? <span className="muted"> ({Math.round(Number(kind.spend_share_today))}%)</span> : null}</div>
          <div><div className="field-label">Records added today</div>{count(kind.added_today)}</div>
          {kind.daily_allowance !== null && kind.daily_allowance !== undefined ? (
            <div><div className="field-label">Daily allowance</div>{allowanceWords(kind)}</div>
          ) : null}
        </div>
      </div>
      {reasons.length ? (
        <div className="panel-section">
          <h3 style={{ margin: "0 0 0.3rem" }}>Why its lookups are queued</h3>
          <table className="tight">
            <tbody>
              {reasons.map((r) => <tr key={r.reason}><td>{r.reason}</td><td className="num">{count(r.n)}</td></tr>)}
            </tbody>
          </table>
        </div>
      ) : null}
      {kind.key !== "other" ? (
        <DepthForm key={`${kind.key}-${kind.searches_per_record}-${kind.results_per_search}`} token={token} kind={kind} onChanged={onChanged} />
      ) : null}
      {kind.daily_allowance !== null && kind.daily_allowance !== undefined ? (
        <AllowanceForm key={`${kind.key}-${kind.daily_allowance}`} token={token} kind={kind} onChanged={onChanged} />
      ) : null}
      {mine.length ? (
        <div className="panel-section">
          {mine.map((sw) => (
            <SwitchRow key={sw.key} token={token} sw={sw} onChanged={onChanged} />
          ))}
        </div>
      ) : null}
    </Panel>
  );
}
